import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material';

interface SummaryEditModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  value: string;
  onSave: (newValue: string) => void;
  maxLength?: number;
  placeholder?: string;
}

const SummaryEditModal: React.FC<SummaryEditModalProps> = ({
  open,
  onClose,
  title,
  value,
  onSave,
  maxLength,
  placeholder = 'No content available',
}) => {
  const [editedValue, setEditedValue] = useState(value);
  const [error, setError] = useState('');

  // Reset edited value when modal opens or value changes
  useEffect(() => {
    if (open) {
      setEditedValue(value);
      setError('');
    }
  }, [open, value]);

  const handleSave = () => {
    // Validate max length
    if (maxLength && editedValue.length > maxLength) {
      setError(`Content must be ${maxLength} characters or less`);
      return;
    }

    onSave(editedValue);
    onClose();
  };

  const handleCancel = () => {
    setEditedValue(value); // Reset to original value
    setError('');
    onClose();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setEditedValue(newValue);

    // Clear error if within limit
    if (maxLength && newValue.length <= maxLength) {
      setError('');
    }
  };

  const isOverLimit = maxLength ? editedValue.length > maxLength : false;
  const characterCount = editedValue.length;
  const remainingChars = maxLength ? maxLength - characterCount : 0;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: maxLength ? '60vh' : '80vh', // Smaller for TLDR/Executive, larger for Detailed
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', pb: 1 }}>
        <TextField
          autoFocus
          multiline
          fullWidth
          value={editedValue}
          onChange={handleChange}
          placeholder={placeholder}
          variant="outlined"
          error={isOverLimit}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              height: '100% !important',
              overflow: 'auto !important',
            },
          }}
        />
        {maxLength && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Typography
              variant="caption"
              color={isOverLimit ? 'error' : 'text.secondary'}
              sx={{ fontWeight: isOverLimit ? 600 : 400 }}
            >
              {isOverLimit 
                ? `${Math.abs(remainingChars)} characters over limit`
                : `${remainingChars} characters remaining`
              }
            </Typography>
          </Box>
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleCancel} 
          color="error"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isOverLimit}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SummaryEditModal;

